import { useState, useEffect, useRef } from 'react';

export default function FullPageScroll() {
  const [currentSection, setCurrentSection] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const containerRef = useRef(null);

  const sections = [
    {
      number: "01",
      category: "⁠INNOVATION AND INTELLIGENCE",
      title: "Engineering the Future with AI.",
      description: "At Foxtroit, we build intelligent systems that learn, adapt, and deliver measurable results. From automation to analytics, our AI-driven approach empowers industries to operate smarter, safer, and faster—transforming challenges into intelligent outcomes.",
      image: "https://images.unsplash.com/photo-1542744094-3a31f272c490?w=1200&h=800&fit=crop",
      sideText: "ERRING EXCELLENCE",
      bgColor: "#f8f9fa"
    },
    {
      number: "02",
      category: "STRATEGY AND EXECUTION",
      title: "Turning Vision into Intelligent Action.",
      description: "We combine deep AI expertise with sharp execution strategy. Our team translates complex business needs into powerful AI solutions that streamline operations, optimize decisions, and unlock hidden potential—because innovation means nothing without impact.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=800&fit=crop",
      sideText: "DELIVERING EXCELLENCE",
      bgColor: "#ffffff"
    },
    {
      number: "03",
      category: "DESIGN AND EXPERIENCE",
      title: "Where Technology Meets Human Insight.",
      description: "AI isn’t just about algorithms—it’s about experience. At Foxtroit, we design interfaces and intelligence that feel natural, intuitive, and human. Our mission is to create technology that not only performs—but also connects, engages, and inspires.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop",
      sideText: "ACHIEVING EXCELLENCE",
      bgColor: "#f8f9fa"
    }
  ];

  useEffect(() => {
    const handleWheel = (e) => {
      if (isScrolling) return;

      e.preventDefault();
      
      setIsScrolling(true);
      
      if (e.deltaY > 0 && currentSection < sections.length - 1) {
        setCurrentSection(prev => prev + 1);
      } else if (e.deltaY < 0 && currentSection > 0) {
        setCurrentSection(prev => prev - 1);
      }

      setTimeout(() => setIsScrolling(false), 1000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [currentSection, isScrolling, sections.length]);

  const handleDotClick = (index) => {
    if (!isScrolling) {
      setCurrentSection(index);
      setIsScrolling(true);
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  return (
    <div ref={containerRef} style={styles.mainContainer}>
      <div 
        style={{
          ...styles.sectionsWrapper,
          transform: `translateY(-${currentSection * 100}vh)`,
        }}
      >
        {sections.map((section, index) => (
          <div key={index} style={styles.section}>
            <div style={styles.leftPanel}>
              <img 
                src={section.image} 
                alt={section.title}
                style={styles.image}
              />
              <div style={styles.overlay}></div>
              <div style={styles.sideTextContainer}>
                <span style={styles.sideText}>{section.sideText}</span>
              </div>
            </div>
            
            <div style={styles.centerLine}></div>
            
            <div style={{...styles.rightPanel, backgroundColor: section.bgColor}}>
              <div style={styles.numberOverlay}>{section.number}</div>
              
              <div style={styles.content}>
                <div style={styles.categoryLine}>
                  <span style={styles.redLine}></span>
                  <span style={styles.category}>{section.category}</span>
                </div>
                
                <h1 style={styles.title}>{section.title}</h1>
                
                <p style={styles.description}>{section.description}</p>
                
                
              </div>

              <div style={styles.scrollText}>SCROLL</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.navigation}>
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            style={{
              ...styles.navDot,
              ...(index === currentSection ? styles.navDotActive : {})
            }}
          />
        ))}
      </div>

      {/* <div style={styles.enquireButton}>
        <span style={styles.enquireText}>Enquire Now</span>
        <span style={styles.enquireArrow}>↑</span>
      </div> */}
    </div>
  );
}

const styles = {
  mainContainer: {
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#fff',
  },
  sectionsWrapper: {
    transition: 'transform 1s cubic-bezier(0.77, 0, 0.175, 1)',
    willChange: 'transform',
  },
  section: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    position: 'relative',
  },
  leftPanel: {
    width: '50%',
    height: '100vh',
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.1) 100%)',
    pointerEvents: 'none',
  },
  centerLine: {
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    transform: 'translateX(-50%)',
  },
  sideTextContainer: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%) rotate(-90deg)',
    transformOrigin: 'center',
    zIndex: 2,
  },
  sideText: {
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '3px',
    color: '#fff',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  rightPanel: {
    width: '50%',
    height: '100vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px',
    transition: 'background-color 1s cubic-bezier(0.77, 0, 0.175, 1)',
  },
  numberOverlay: {
    position: 'absolute',
    top: '80px',
    right: '80px',
    fontSize: '180px',
    fontWeight: '700',
    color: 'rgba(0, 0, 0, 0.03)',
    lineHeight: '1',
    userSelect: 'none',
  },
  content: {
    maxWidth: '600px',
    zIndex: 1,
  },
  categoryLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '30px',
  },
  redLine: {
    width: '40px',
    height: '2px',
    backgroundColor: '#e63946',
  },
  category: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '2px',
    color: '#e63946',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '56px',
    fontWeight: '700',
    lineHeight: '1.1',
    marginBottom: '30px',
    color: '#000',
  },
  description: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#666',
    marginBottom: '40px',
  },
  button: {
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    padding: '18px 40px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.3s',
  },
  arrow: {
    fontSize: '20px',
    transition: 'transform 0.3s',
  },
  scrollText: {
    position: 'absolute',
    right: '40px',
    bottom: '40px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '3px',
    color: '#000',
    transform: 'rotate(90deg)',
    transformOrigin: 'right bottom',
  },
  navigation: {
    position: 'fixed',
    right: '40px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    zIndex: 100,
  },
  navDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: '2px solid #000',
    cursor: 'pointer',
    transition: 'all 0.3s',
    padding: 0,
  },
  navDotActive: {
    backgroundColor: '#000',
    transform: 'scale(1.2)',
  },
  enquireButton: {
    position: 'fixed',
    bottom: '40px',
    right: '40px',
    backgroundColor: '#000',
    color: '#fff',
    padding: '18px 30px',
    borderRadius: '50px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    zIndex: 100,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  enquireText: {
    fontSize: '15px',
    fontWeight: '600',
  },
  enquireArrow: {
    fontSize: '20px',
    fontWeight: '700',
  },
};